<script setup lang="ts">
import {
  BNavbar,
  BNavbarBrand,
  BNavbarToggle,
  BCollapse,
  BNavbarNav,
} from 'bootstrap-vue-next';
import ItpNavMenu from './ItpNavMenu.vue';
import type { BaseColorVariant } from 'bootstrap-vue-next';
import type { NavItem } from './types';
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useSidebarLayout } from '../sidebar';

type BaseBgColorVariant = keyof BaseColorVariant;

const props = withDefaults(
  defineProps<{
    brand: string;
    items?: NavItem[];
    rightItems?: NavItem[];
    variant?: BaseBgColorVariant;
    bg?: string;
    container?: boolean;
    sticky?: boolean;
    fixed?: 'top' | 'bottom';
  }>(),
  {
    items: () => [],
    rightItems: () => [],
    variant: 'light',
    bg: 'light',
    container: false,
    sticky: false,
    fixed: undefined,
  },
);

const emit = defineEmits<{
  (event: 'navigate', item: NavItem): void;
  (event: 'action', item: NavItem): void;
}>();

const navElement = ref<HTMLElement | null>(null);
const { setNavHeight } = useSidebarLayout();

let resizeObserver: ResizeObserver | null = null;
onMounted(() => {
  if (!navElement.value) {
    return;
  }
  const update = () => setNavHeight(navElement.value!.offsetHeight);
  update();
  resizeObserver = new ResizeObserver(update);
  resizeObserver.observe(navElement.value);
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
});
</script>

<template>
  <BNavbar
    ref="navElement"
    :variant="variant"
    :bg="bg"
    :sticky="sticky ? 'top' : undefined"
    :fixed="fixed"
    toggleable="lg"
    class="shadow-sm navbar--itp"
  >
    <div
      :class="container ? 'container' : ''"
      class="w-100 d-flex align-items-center"
    >
      <BNavbarBrand class="me-3">
        <slot name="brand">
          <RouterLink to="/" class="text-decoration-none">
            {{ brand }}
          </RouterLink>
        </slot>
      </BNavbarBrand>

      <BNavbarToggle target="main-nav" class="ms-auto d-lg-none" />

      <BCollapse id="main-nav" is-nav>
        <div class="d-flex w-100 flex-column flex-lg-row">
          <BNavbarNav class="me-auto">
            <ItpNavMenu
              v-for="item in items"
              :key="item.id || item.label"
              :item="item"
              @navigate="emit('navigate', $event)"
              @action="emit('action', $event)"
            >
              <template #icon="{ item }">
                <iconify-icon v-if="item.icon" :icon="item.icon" class="me-1" />
              </template>
            </ItpNavMenu>
          </BNavbarNav>

          <BNavbarNav class="ms-lg-auto">
            <ItpNavMenu
              v-for="item in rightItems"
              :key="item.id || item.label"
              :item="item"
              align="end"
              @navigate="emit('navigate', $event)"
              @action="emit('action', $event)"
            >
              <template #icon="{ item }">
                <iconify-icon v-if="item.icon" :icon="item.icon" class="me-1" />
              </template>
            </ItpNavMenu>
          </BNavbarNav>
        </div>
      </BCollapse>
    </div>
  </BNavbar>
</template>

<style scoped>
.navbar--itp {
  background-color: #fff !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.navbar--itp .navbar-brand {
  font-weight: 600;
  letter-spacing: 0.2px;
}

.navbar--itp .navbar-nav .nav-link {
  padding-left: 0.6rem;
  padding-right: 0.6rem;
}

.navbar--itp .dropdown-menu {
  border-radius: 0.5rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.06);
  padding: 0.4rem;
}

.navbar--itp .dropdown-item {
  border-radius: 0.35rem;
}

@media (min-width: 992px) {
  .navbar--itp .navbar-nav {
    gap: 0.25rem;
  }
}
:deep(.navbar--itp .dropdown-menu) {
  max-height: calc(100vh - var(--itp-nav-h) - 16px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}
</style>
